import { useState, useCallback, useEffect, useRef } from "react";
import {
	getProctorToken,
	getProctorAttempts,
	PROCTOR_URL,
	PROCTOR_PROVIDER,
	ProctorAttemptsResponse,
} from "../api/ProctorAPI";

// Добавляем глобальную переменную для identifier
declare global {
	interface Window {
		__proctoringIdentifier?: string | null;
	}
}

export type ProctorStatus = 
	| "idle"
	| "initializing"
	| "ready"
	| "running"
	| "stopping"
	| "stopped"
	| "error";

export interface UseProctoring {
	status: ProctorStatus;
	sessionId: string | null;
	proctoringIdentifier: string | null;
	error: string | null;
	attempts: ProctorAttemptsResponse | null;
	isDesktop: boolean;
	isBlocked: boolean;
	initProctoring: () => Promise<boolean>;
	startSession: () => Promise<string | null>;
	stopSession: () => Promise<void>;
	loadAttempts: () => Promise<void>;
	refreshAttempts: () => Promise<void>;
}

const DESKTOP_MIN_WIDTH = 910;

// Глобальный инстанс Supervisor - не зависит от жизненного цикла компонента
declare global {
	interface Window {
		__proctoringSupervisor?: Supervisor | null;
		__proctoringSessionId?: string | null;
	}
}

export const useProctoring = (): UseProctoring => {
	const [status, setStatus] = useState<ProctorStatus>(() => {
		// Восстанавливаем статус если сессия уже запущена
		if (window.__proctoringSupervisor && window.__proctoringSessionId) {
			return "running";
		}
		return "idle";
	});
	const [sessionId, setSessionId] = useState<string | null>(
		window.__proctoringSessionId || null
	);
	const [proctoringIdentifier, setProctoringIdentifier] = useState<string | null>(
		window.__proctoringIdentifier || null
	);
	const [error, setError] = useState<string | null>(null);
	const [attempts, setAttempts] = useState<ProctorAttemptsResponse | null>(null);
	const [isDesktop, setIsDesktop] = useState(
		window.innerWidth >= DESKTOP_MIN_WIDTH
	);
	
	// Отслеживаем mounted state для предотвращения memory leak
	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Отслеживание ширины экрана
	useEffect(() => {
		const handleResize = () => {
			setIsDesktop(window.innerWidth >= DESKTOP_MIN_WIDTH);
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Загрузка информации о попытках
	const loadAttempts = useCallback(async () => {
		try {
			const data = await getProctorAttempts();
			if (isMountedRef.current) {
				setAttempts(data);
			}
		} catch (err) {
			console.error("Failed to load proctoring attempts:", err);
		}
	}, []);

	// Инициализация прокторинга
	const initProctoring = useCallback(async (): Promise<boolean> => {
		console.log("[Proctoring] Starting initialization...");
		
		// Если уже есть активная сессия, не инициализируем заново
		if (window.__proctoringSupervisor && window.__proctoringSessionId) {
			console.log("[Proctoring] Already initialized, session:", 
				window.__proctoringSessionId);
			if (isMountedRef.current) {
				setStatus("running");
				setSessionId(window.__proctoringSessionId);
				setProctoringIdentifier(window.__proctoringIdentifier || null);
			}
			return true;
		}
		
		// Очищаем старый supervisor если он есть (сессия завершена)
		if (window.__proctoringSupervisor) {
			console.log("[Proctoring] Cleaning up old supervisor...");
			try {
				window.__proctoringSupervisor.close();
			} catch (err) {
				console.warn("[Proctoring] Error closing old supervisor:", err);
			}
			window.__proctoringSupervisor = null;
			window.__proctoringIdentifier = null;
		}
		
		if (!isDesktop) {
			setError("Прокторинг доступен только на ПК");
			return false;
		}

		if (!window.Supervisor) {
			console.error("[Proctoring] SDK not loaded");
			setError("SDK прокторинга не загружен");
			return false;
		}

		setStatus("initializing");
		setError(null);

		try {
			console.log("[Proctoring] Fetching JWT token...");
			const tokenData = await getProctorToken();
			console.log("[Proctoring] Token received:", tokenData.token ? "OK" : "EMPTY");
			console.log("[Proctoring] Identifier:", tokenData.proctoring_identifier);
			
			// Сохраняем identifier
			window.__proctoringIdentifier = tokenData.proctoring_identifier;
			if (isMountedRef.current) {
				setProctoringIdentifier(tokenData.proctoring_identifier);
			}

			console.log("[Proctoring] Creating NEW Supervisor instance...");
			const supervisor = new window.Supervisor({ url: PROCTOR_URL });
			
			// Сохраняем в глобальную переменную
			window.__proctoringSupervisor = supervisor;
			console.log("[Proctoring] Supervisor created, URL:", PROCTOR_URL);

			// Подписываемся на события (с проверкой mounted)
			supervisor.on("error", (data) => {
				console.error("[Proctoring] Error event:", data);
				if (isMountedRef.current) {
					setError("Ошибка прокторинга");
					setStatus("error");
				}
			});

			supervisor.on("start", (data) => {
				console.log("[Proctoring] Start event:", data);
				if (data?.session?.id) {
					window.__proctoringSessionId = data.session.id;
					if (isMountedRef.current) {
						setSessionId(data.session.id);
					}
				}
				if (isMountedRef.current) {
					setStatus("running");
				}
			});

			supervisor.on("stop", () => {
				console.log("[Proctoring] Stop event");
				window.__proctoringSessionId = null;
				if (isMountedRef.current) {
					setStatus("stopped");
				}
			});

			console.log("[Proctoring] Calling supervisor.init()...");
			await supervisor.init({
				provider: PROCTOR_PROVIDER,
				token: tokenData.token,
			});
			console.log("[Proctoring] Init completed successfully");

			setStatus("ready");
			return true;
		} catch (err) {
			console.error("[Proctoring] Init error:", err);
			const errorMessage = err instanceof Error 
				? err.message 
				: "Ошибка инициализации";
			setError(errorMessage);
			setStatus("error");
			return false;
		}
	}, [isDesktop]);

	// Запуск сессии прокторинга
	const startSession = useCallback(async (): Promise<string | null> => {
		console.log("[Proctoring] Starting session...");
		
		const supervisor = window.__proctoringSupervisor;
		
		if (!supervisor) {
			console.error("[Proctoring] Supervisor not initialized");
			setError("Прокторинг не инициализирован");
			return null;
		}

		try {
			console.log("[Proctoring] Calling supervisor.start()...");
			const session = await supervisor.start();
			console.log("[Proctoring] Start result:", session);
			
			if (session && session.id) {
				console.log("[Proctoring] Session ID from start():", session.id);
				setSessionId(session.id);
				window.__proctoringSessionId = session.id;
				setStatus("running");
				return session.id;
			}
			
			console.log("[Proctoring] Trying lookup()...");
			const lookupSession = supervisor.lookup();
			console.log("[Proctoring] Lookup result:", lookupSession);
			
			if (lookupSession && lookupSession.id) {
				console.log("[Proctoring] Session ID from lookup():", lookupSession.id);
				setSessionId(lookupSession.id);
				window.__proctoringSessionId = lookupSession.id;
				setStatus("running");
				return lookupSession.id;
			}
			
			console.warn("[Proctoring] No session ID available, using timestamp");
			const tempId = `proctoring_${Date.now()}`;
			setSessionId(tempId);
			window.__proctoringSessionId = tempId;
			setStatus("running");
			return tempId;
		} catch (err) {
			console.error("[Proctoring] Start error:", err);
			const errorMessage = err instanceof Error 
				? err.message 
				: "Ошибка запуска сессии";
			setError(errorMessage);
			setStatus("error");
			return null;
		}
	}, []);

	// Остановка сессии прокторинга (вызывать только при завершении экзамена!)
	const stopSession = useCallback(async (): Promise<void> => {
		console.log("[Proctoring] Stopping session...");
		
		const supervisor = window.__proctoringSupervisor;
		if (!supervisor) {
			console.log("[Proctoring] No active supervisor to stop");
			return;
		}

		setStatus("stopping");

		try {
			await supervisor.stop();
			await supervisor.logout();
			supervisor.close();
			setStatus("stopped");
			console.log("[Proctoring] Session stopped successfully");
		} catch (err) {
			console.error("[Proctoring] Error stopping session:", err);
			setStatus("error");
		} finally {
			window.__proctoringSupervisor = null;
			window.__proctoringSessionId = null;
			window.__proctoringIdentifier = null;
			setSessionId(null);
			setProctoringIdentifier(null);
		}
	}, []);

	// Функция для перезагрузки попыток (после сброса администратором)
	const refreshAttempts = useCallback(async () => {
		await loadAttempts();
	}, [loadAttempts]);

	// НЕ закрываем supervisor при размонтировании компонента!
	// Он должен работать пока идёт экзамен

	// Вычисляем заблокирован ли пользователь
	const isBlocked = attempts?.is_blocked || 
		(attempts?.remaining_attempts !== undefined && attempts.remaining_attempts <= 0);

	return {
		status,
		sessionId,
		proctoringIdentifier,
		error,
		attempts,
		isDesktop,
		isBlocked: !!isBlocked,
		initProctoring,
		startSession,
		stopSession,
		loadAttempts,
		refreshAttempts,
	};
};

export default useProctoring;
